import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card, Alert } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IBillingInfo } from "src/app-telehealth-demo/util/utils";
import { STATE_OPTIONS } from "src/constants/regions";

interface BillingInfoStepProps {
    billingInfo: Partial<IBillingInfo>;
    updateBillingInfo: (data: Partial<IBillingInfo>) => void;
    patientInfo: any; // Personal address to copy from if same as billing
    onContinue: () => void;
    onBack: () => void;
}

// Form validation schema
const schema = yup.object().shape({
    sameAsPersonal: yup.boolean(),
    billingAddress: yup.string().when("sameAsPersonal", {
        is: (val: boolean) => val === false,
        then: (schema) => schema.required("Billing address is required"),
        otherwise: (schema) => schema.optional(),
    }),
    billingAddress2: yup.string().optional(),
    billingCity: yup.string().when("sameAsPersonal", {
        is: (val: boolean) => val === false,
        then: (schema) => schema.required("City is required"),
        otherwise: (schema) => schema.optional(),
    }),
    billingState: yup.string().when("sameAsPersonal", {
        is: (val: boolean) => val === false,
        then: (schema) => schema.required("State is required"),
        otherwise: (schema) => schema.optional(),
    }),
    billingZip: yup.string().when("sameAsPersonal", {
        is: (val: boolean) => val === false,
        then: (schema) =>
            schema
                .matches(/^\d{5}(-\d{4})?$/, "ZIP code must be 5 digits or 5+4 format")
                .required("ZIP code is required"),
        otherwise: (schema) => schema.optional(),
    }),
});
const BillingInfoStep: React.FC<BillingInfoStepProps> = ({
    billingInfo,
    updateBillingInfo,
    patientInfo,
    onContinue,
    onBack,
}) => {
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: {
            sameAsPersonal:
                billingInfo.sameAsPersonal !== undefined ? billingInfo.sameAsPersonal : true,
            billingAddress: billingInfo.billingAddress || "",
            billingAddress2: billingInfo.billingAddress2 || "",
            billingCity: billingInfo.billingCity || "",
            billingState: billingInfo.billingState || "",
            billingZip: billingInfo.billingZip || "",
        },
    });

    const sameAsPersonal = watch("sameAsPersonal");

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (type === "change") {
                updateBillingInfo(value);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, updateBillingInfo]);

    // When same as personal is checked, copy address from patient info
    useEffect(() => {
        if (sameAsPersonal && patientInfo) {
            setValue("billingAddress", patientInfo.address || "");
            setValue("billingAddress2", patientInfo.address2 || "");
            setValue("billingCity", patientInfo.city || "");
            setValue("billingState", patientInfo.state || "");
            setValue("billingZip", patientInfo.zip || "");
        }
    }, [sameAsPersonal, patientInfo, setValue]);

    const onSubmit = (data: IBillingInfo) => {
        // Update billing info in parent component
        updateBillingInfo(data);
        // Continue to next step
        onContinue();
    };

    return (
        <div className="billing-info-step">
            <h2 className="mb-4">Billing Address</h2>
            <p className="text-muted mb-4">Please provide your billing address information.</p>

            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Card className="mb-4">
                    <Card.Body>
                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                id="same-as-personal"
                                label="Billing address is the same as my personal address"
                                {...register("sameAsPersonal")}
                            />
                        </Form.Group>

                        {!sameAsPersonal && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Billing Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        isInvalid={!!errors.billingAddress}
                                        {...register("billingAddress")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.billingAddress?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Address Line 2 (Optional)</Form.Label>
                                    <Form.Control type="text" {...register("billingAddress2")} />
                                </Form.Group>

                                <Row>
                                    <Col md={5}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>City</Form.Label>
                                            <Form.Control
                                                type="text"
                                                isInvalid={!!errors.billingCity}
                                                {...register("billingCity")}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.billingCity?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>State</Form.Label>
                                            <Form.Select
                                                isInvalid={!!errors.billingState}
                                                {...register("billingState")}
                                            >
                                                <option value="">Select State</option>
                                                {STATE_OPTIONS.filter((option) => option.value).map(
                                                    (state) => (
                                                        <option
                                                            key={state.value}
                                                            value={state.value}
                                                        >
                                                            {state.label}
                                                        </option>
                                                    ),
                                                )}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.billingState?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>ZIP Code</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="12345"
                                                isInvalid={!!errors.billingZip}
                                                {...register("billingZip")}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.billingZip?.message}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                    <Button variant="outline-secondary" onClick={onBack}>
                        Back
                    </Button>
                    <Button variant="primary" type="submit">
                        Continue
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default BillingInfoStep;
